import { toast } from 'react-toastify';
import { all, call, fork, put, takeEvery, takeLatest } from 'redux-saga/effects';
import { ApiResponse } from 'api/ApiClient';
import { SuccessToastContainer } from 'components/Toast/ToastContainer';
import { IAction, IPayPeriodClosure, IPayPeriods, PayPeriodAction } from 'types';
import { isStalePayPeriodError } from 'utils/payPeriods';
import * as API from '../../../api';
import * as Actions from './actions';
import { IGetPayPeriodsBetweenPayload, IGetPayPeriodsPayload, IUpdatePayPeriodStatusPayload, PAY_PERIODS_PAGE_SIZE, PayPeriods } from './types';

export function* getPayPeriods({ payload }: IAction<PayPeriods, IGetPayPeriodsPayload>): any {
  const { siteId, orgId, before, from } = payload;
  try {
    // With a selected period, every period from it to today; otherwise the newest ten, or the ten before the cursor.
    const query = from ? { start: from } : { before, limit: PAY_PERIODS_PAGE_SIZE };
    const response: ApiResponse<IPayPeriods> = yield call(API.Sites.getPayPeriod, siteId, query);
    // The range answers oldest first; the store keeps newest first.
    const data = from ? [...response.body.data].reverse() : response.body.data;

    yield put(Actions.getPayPeriodsSuccess({ response: { ...response.body, data }, siteId, orgId }));
  } catch (error: any) {
    yield put(Actions.getPayPeriodsError(error));
  }
}

export function* getPayPeriodsBetween({ payload }: IAction<PayPeriods, IGetPayPeriodsBetweenPayload>): any {
  const { siteId, start, end, onSuccess } = payload;
  try {
    const response: ApiResponse<IPayPeriods> = yield call(API.Sites.getPayPeriod, siteId, { start, end });

    yield call(onSuccess, response.body.data);
    yield put(Actions.getPayPeriodsBetweenSuccess());
  } catch (error: any) {
    yield put(Actions.getPayPeriodsBetweenError(error));
  }
}

const STATUS_TOAST: Record<PayPeriodAction, { success: string; failure: string }> = {
  [PayPeriodAction.CLOSE]: { success: 'Pay Period closed successfully', failure: 'Unable to close Pay Period' },
  [PayPeriodAction.REOPEN]: { success: 'Pay Period opened successfully', failure: 'Unable to reopen Pay Period' },
};

export function* updatePayPeriodStatus({ payload }: IAction<PayPeriods, IUpdatePayPeriodStatusPayload>): any {
  const { siteId, orgId, payPeriod, status, onComplete } = payload;
  let closure: IPayPeriodClosure | undefined;
  try {
    const response: ApiResponse<IPayPeriodClosure> = yield call(API.Sites.updatePayPeriodStatus, siteId, { startDate: payPeriod.start, endDate: payPeriod.end, status });
    closure = response.body;

    yield put(Actions.updatePayPeriodStatusSuccess({ siteId, orgId, response: closure }));
    yield call(toast, SuccessToastContainer({ message: STATUS_TOAST[status].success }));
  } catch (error: any) {
    const message = (isStalePayPeriodError(error) && error.body?.errors?.[0]?.msg) || STATUS_TOAST[status].failure;
    yield put(Actions.updatePayPeriodStatusError(error, message));
  } finally {
    // The modal dismisses either way; the closure tells the screen whether anything changed.
    if (onComplete) yield call(onComplete, closure);
  }
}

export function* watchGetPayPeriods() {
  yield takeEvery(PayPeriods.GET_PAY_PERIODS_REQUEST, getPayPeriods);
}

export function* watchGetPayPeriodsBetween() {
  yield takeLatest(PayPeriods.GET_PAY_PERIODS_BETWEEN_REQUEST, getPayPeriodsBetween);
}

export function* watchUpdatePayPeriodStatus() {
  yield takeEvery(PayPeriods.UPDATE_PAY_PERIOD_STATUS_REQUEST, updatePayPeriodStatus);
}

function* payPeriodsSaga() {
  yield all([fork(watchGetPayPeriods), fork(watchGetPayPeriodsBetween), fork(watchUpdatePayPeriodStatus)]);
}
export { payPeriodsSaga };
